import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { formatIQD } from '../../../shared/format.js';

// نافذة العرض الافتراضية: ساعات العمل، وتتوسّع تلقائياً لأي ساعة فيها
// إيراد فعلي خارجها فلا يختفي مبلغ عن المستخدم.
const DEFAULT_FIRST = 8;
const DEFAULT_LAST = 23;

function formatYAxis(v) {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}م`;
  if (v >= 1_000) return `${Math.round(v / 1_000)}ك`;
  return v;
}

// الخادم يعيد { date, period, days, buckets: [{hour, play, cafeteria, toys,
// total}], totals }. قراءة الكائن مباشرة كمصفوفة كانت تعطي صفراً دائماً
// فيظهر الرسم فارغاً رغم وجود مبيعات.
function rowsOf(data) {
  if (Array.isArray(data)) return data;
  return data?.buckets || data?.items || data?.data || [];
}

function hourLabel(h) {
  if (typeof h === 'number' && Number.isFinite(h)) return `${String(h).padStart(2, '0')}:00`;
  return h ? String(h) : '';
}

function normalize(data) {
  const arr = rowsOf(data);
  const byHour = new Map();
  arr.forEach((d, i) => {
    const parsed = typeof d.hour === 'number' ? d.hour : Number.parseInt(d.hour ?? i, 10);
    const key = Number.isFinite(parsed) ? parsed : i;
    byHour.set(key, {
      h: key,
      play: Number(d.play ?? d.entry ?? d.gate ?? 0) + Number(d.manual ?? 0),
      cafeteria: Number(d.cafeteria ?? d.cafe ?? 0),
      toys: Number(d.toys ?? d.shop ?? 0)
    });
  });

  const withData = [...byHour.values()].filter((r) => r.play || r.cafeteria || r.toys);
  const first = Math.min(DEFAULT_FIRST, ...withData.map((r) => r.h));
  const last = Math.max(DEFAULT_LAST, ...withData.map((r) => r.h));

  const out = [];
  for (let h = first; h <= last; h += 1) {
    const row = byHour.get(h) || {
      h, play: 0, cafeteria: 0, toys: 0
    };
    out.push({
      hour: hourLabel(h), play: row.play, cafeteria: row.cafeteria, toys: row.toys
    });
  }
  return out;
}

export function HourlyChart({ data, height = 260 }) {
  const rows = normalize(data);
  const empty = rows.every((r) => !r.play && !r.cafeteria && !r.toys);
  return (
    <div className="relative" style={{ height }} data-testid="reports-hourly-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 10, right: 8, left: 8, bottom: 4 }}>
          <CartesianGrid stroke="#F0E6DA" vertical={false} />
          <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
          <YAxis
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={formatYAxis}
            width={42}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,122,26,.06)' }}
            formatter={(v) => formatIQD(v)}
            contentStyle={{
              background: '#fff', border: '1px solid #EFE7D6', borderRadius: 10, fontSize: 12
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11.5, paddingTop: 6 }} iconType="circle" iconSize={8} />
          <Bar dataKey="play" name="لعب" stackId="a" fill="#FF7A1A" />
          <Bar dataKey="cafeteria" name="كافتيريا" stackId="a" fill="#FFB627" />
          <Bar dataKey="toys" name="ألعاب" stackId="a" fill="#EC4899" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      {empty ? (
        <div className="absolute inset-0 grid place-items-center text-ink-3 text-xs pointer-events-none">
          لا توجد بيانات بعد
        </div>
      ) : null}
    </div>
  );
}
